import React, { useState, useMemo } from "react";
import "./scatterChart.css";

export default function ScatterChart({ selectedPeriod = "90D", data }) {
  const [activeTab, setActiveTab] = useState("active");

  if (!data) return null;

  const vehicleInfo = data?.vehicle_info || {};
  const pricingInfo = data?.pricing_info || {};
  const searchMetadata = data?.search_metadata || {};
  const hooptiePrice = pricingInfo?.marketcheck_price || pricingInfo?.hooptie_price || searchMetadata?.asking_price || 0;
  const hooptieMileage = searchMetadata?.mileage || vehicleInfo?.mileage || 0;

  // Hooptie vehicle data (main dot)
  const hooptieVehicle = {
    price: hooptiePrice,
    mileage: hooptieMileage,
  };

  // Filter data by time period
  const filterByPeriod = useMemo(() => {
    const now = new Date();
    let daysAgo = 30;
    if (selectedPeriod === "60D") daysAgo = 60;
    else if (selectedPeriod === "90D") daysAgo = 90;
    
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
  }, [selectedPeriod]);

  const activeListings = data?.active_listings || data?.market_analysis?.active_listings || [];
  const recentSales = data?.market_analysis?.recent_sales || [];
  const RADIUS_MI = 100;

  // Transform active_listings from API data (within 100 mile radius; include listings without distance)
  const activeData = useMemo(() => {
    return activeListings
      .filter(listing => {
        const d = listing.distance_miles ?? listing.distance_mi ?? listing.distance;
        return d == null || d <= RADIUS_MI;
      })
      .filter(listing => !filterByPeriod || filterByPeriod(listing.first_seen_at_date))
      .map(listing => ({
        price: listing.price,
        mileage: listing.miles || listing.mileage
      }));
  }, [activeListings, filterByPeriod]);

  // Transform recent_sales from API data
  const soldData = useMemo(() => {
    return recentSales
      .filter(sale => filterByPeriod(sale.last_seen_at_date || sale.sold_date))
      .map(sale => ({
        price: sale.price,
        mileage: sale.miles
      }));
  }, [filterByPeriod]);

  const currentData = activeTab === "active" ? activeData : soldData;

  // If no data, return early
  if (currentData.length === 0) {
    return (
      <div className="scatter-chart">
        <div className="scatter-tabs">
          <button
            className={`scatter-tab ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active
          </button>
          <button
            className={`scatter-tab ${activeTab === "sold" ? "active" : ""}`}
            onClick={() => setActiveTab("sold")}
          >
            Sold
          </button>
        </div>
        <div className="scatter-no-data">No data available for the selected time period.</div>
      </div>
    );
  }

  // Calculate chart dimensions and scales
  const minPrice = Math.min(...currentData.map(d => d.price), hooptieVehicle.price) - 500;
  const maxPrice = Math.max(...currentData.map(d => d.price), hooptieVehicle.price) + 500;
  const minMileage = Math.min(...currentData.map(d => d.mileage), hooptieVehicle.mileage) - 5000;
  const maxMileage = Math.max(...currentData.map(d => d.mileage), hooptieVehicle.mileage) + 5000;

  const chartWidth = 600;
  const chartHeight = 300;
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };

  const scaleX = (mileage) => {
    return ((mileage - minMileage) / (maxMileage - minMileage)) * (chartWidth - padding.left - padding.right) + padding.left;
  };

  const scaleY = (price) => {
    return chartHeight - padding.bottom - ((price - minPrice) / (maxPrice - minPrice)) * (chartHeight - padding.top - padding.bottom);
  };

  // Generate Y-axis labels (price)
  const priceSteps = 5;
  const priceStep = (maxPrice - minPrice) / priceSteps;
  const priceLabels = [];
  for (let i = 0; i <= priceSteps; i++) {
    priceLabels.push(minPrice + priceStep * i);
  }

  // Generate X-axis labels (mileage)
  const mileageSteps = 5;
  const mileageStep = (maxMileage - minMileage) / mileageSteps;
  const mileageLabels = [];
  for (let i = 0; i <= mileageSteps; i++) {
    mileageLabels.push(minMileage + mileageStep * i);
  }

  return (
    <div className="scatter-chart">
      <div className="scatter-tabs">
        <button
          className={`scatter-tab ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          Active
        </button>
        <button
          className={`scatter-tab ${activeTab === "sold" ? "active" : ""}`}
          onClick={() => setActiveTab("sold")}
        >
          Sold
        </button>
      </div>

      <div className="scatter-chart-container">
        <svg className="scatter-svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          {priceLabels.map((price, i) => (
            <line
              key={`price-grid-${i}`}
              x1={padding.left}
              y1={scaleY(price)}
              x2={chartWidth - padding.right}
              y2={scaleY(price)}
              stroke="rgba(255, 255, 255, 0.05)"
              strokeWidth="1"
            />
          ))}
          {mileageLabels.map((mileage, i) => (
            <line
              key={`mileage-grid-${i}`}
              x1={scaleX(mileage)}
              y1={padding.top}
              x2={scaleX(mileage)}
              y2={chartHeight - padding.bottom}
              stroke="rgba(255, 255, 255, 0.05)"
              strokeWidth="1"
            />
          ))}

          {/* Y-axis labels (Price) */}
          {priceLabels.map((price, i) => (
            <text
              key={`price-label-${i}`}
              x={padding.left - 10}
              y={scaleY(price) + 4}
              fill="rgba(255, 255, 255, 0.4)"
              fontSize="10"
              textAnchor="end"
            >
              ${(price / 1000).toFixed(1)}k
            </text>
          ))}

          {/* X-axis labels (Mileage) */}
          {mileageLabels.map((mileage, i) => (
            <text
              key={`mileage-label-${i}`}
              x={scaleX(mileage)}
              y={chartHeight - padding.bottom + 20}
              fill="rgba(255, 255, 255, 0.4)"
              fontSize="10"
              textAnchor="middle"
            >
              {(mileage / 1000).toFixed(0)}k
            </text>
          ))}

          {/* Axis labels */}
          <text
            x={chartWidth / 2}
            y={chartHeight - 5}
            fill="rgba(255, 255, 255, 0.6)"
            fontSize="11"
            textAnchor="middle"
            fontWeight="600"
          >
            Mileage
          </text>
          <text
            x={15}
            y={chartHeight / 2}
            fill="rgba(255, 255, 255, 0.6)"
            fontSize="11"
            textAnchor="middle"
            fontWeight="600"
            transform={`rotate(-90, 15, ${chartHeight / 2})`}
          >
            Price
          </text>

          {/* Data points */}
          {currentData.map((point, index) => {
            const fillColor = activeTab === "active" ? "rgba(96, 165, 250, 0.6)" : "rgba(251, 191, 36, 0.6)";
            const strokeColor = activeTab === "active" ? "rgba(96, 165, 250, 0.9)" : "rgba(251, 191, 36, 0.9)";
            return (
              <circle
                key={`point-${index}`}
                cx={scaleX(point.mileage)}
                cy={scaleY(point.price)}
                r="4"
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth="1"
              />
            );
          })}

          {/* Hooptie vehicle (main dot) */}
          <circle
            cx={scaleX(hooptieVehicle.mileage)}
            cy={scaleY(hooptieVehicle.price)}
            r="8"
            fill="rgba(83, 223, 155, 0.8)"
            stroke="rgba(83, 223, 155, 1)"
            strokeWidth="2"
            className="hooptie-dot"
          />
          <circle
            cx={scaleX(hooptieVehicle.mileage)}
            cy={scaleY(hooptieVehicle.price)}
            r="12"
            fill="none"
            stroke="rgba(83, 223, 155, 0.3)"
            strokeWidth="1"
            className="hooptie-dot-ring"
          />
        </svg>
      </div>

      <div className="scatter-legend">
        <div className="legend-item">
          <span 
            className="legend-dot legend-data" 
            style={{
              background: activeTab === "active" ? "rgba(96, 165, 250, 0.6)" : "rgba(251, 191, 36, 0.6)",
              borderColor: activeTab === "active" ? "rgba(96, 165, 250, 0.9)" : "rgba(251, 191, 36, 0.9)"
            }}
          ></span>
          <span>{activeTab === "active" ? "Active" : "Sold"} Market</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot legend-hooptie"></span>
          <span>Hooptie Vehicle</span>
        </div>
      </div>
    </div>
  );
}

